import "./DailyBriefing.scss";
import React from "react";
import {observer} from "mobx-react-lite";
import {Button, ButtonBase, IconButton, Menu, MenuItem, Popover} from "@mui/material";
import {
    AutoAwesomeOutlined,
    CheckCircleOutline,
    ChevronLeft,
    ChevronRight,
    Close,
    ExpandMore,
    PaidOutlined,
    ScheduleOutlined,
    TrendingUp,
} from "@mui/icons-material";
import {MainPageFacade} from "../../components/main/page/MainPageFacade.tsx";
import {BRIEFING} from "./briefingData";
import {formatCompactEuro} from "./briefingLogic";
import {BriefingProvider, useBriefing} from "./briefingState";
import {OpportunityCard} from "./components/OpportunityCard.tsx";
import {AtlasCompanion} from "./components/AtlasCompanion.tsx";
import {AtlasHero} from "./components/AtlasHero.tsx";
import {NeedsConfirmationDialog} from "./components/NeedsConfirmationDialog.tsx";
import AtlasIcon from "../../components/icons/svg/AtlasIcon.svg?react";

/**
 * Home > Daily Briefing — a ten-second morning scan, not a workspace.
 * Every display value comes from daily-briefing.fixture.json (plus in-session
 * recalculation); the layout mirrors the approved design: Atlas hero, section
 * header with scope meta, the ambient sentence, counter chips, the list, and
 * the blocked opportunity as a quiet card below.
 */
const DailyBriefingContent: React.FC = observer(() => {
    const {state, view, dispatch} = useBriefing();
    const session = BRIEFING.session;

    const listRef = React.useRef<HTMLDivElement>(null);
    const [listScroll, setListScroll] = React.useState({canPrev: false, canNext: true});
    const [switcherAnchor, setSwitcherAnchor] = React.useState<null | HTMLElement>(null);
    const [counterAnchor, setCounterAnchor] = React.useState<{id: string, el: HTMLElement} | null>(null);
    const [needsConfirmationOpen, setNeedsConfirmationOpen] = React.useState(false);

    // One step = a regular card plus the list gap.
    const listStep = (el: HTMLElement): number => {
        const card = el.children[0] as HTMLElement | undefined;
        return (card?.offsetWidth ?? 560) + 20;
    };

    const nudgeList = (direction: 1 | -1) => {
        const el = listRef.current;
        if (!el) return;
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        el.scrollBy({left: direction * listStep(el), behavior: reduceMotion ? 'auto' : 'smooth'});
    };

    const onListScroll = React.useCallback(() => {
        const el = listRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        setListScroll({
            canPrev: el.scrollLeft > 4,
            canNext: el.scrollLeft < maxScroll - 4,
        });
    }, []);

    React.useEffect(() => {
        onListScroll();
        window.addEventListener('resize', onListScroll);
        return () => window.removeEventListener('resize', onListScroll);
    }, [onListScroll]);

    const onChipClick = (id: string, el: HTMLElement) => {
        if (id === 'opportunities') {
            listRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
        } else {
            setCounterAnchor({id, el});
        }
    };

    const chips = [
        {id: 'opportunities', icon: <AutoAwesomeOutlined/>, label: 'Ready to act', value: String(view.readyToAct.length)},
        {id: 'savings', icon: <TrendingUp/>, label: 'Savings identified', value: formatCompactEuro(view.savingsTotal)},
        {id: 'cash', icon: <PaidOutlined/>, label: 'Cash release', value: formatCompactEuro(view.cashTotal)},
        {id: 'waiting', icon: <ScheduleOutlined/>, label: 'Waiting on you', value: String(BRIEFING.initiatives.tasks_waiting.length)},
    ];

    const counterPopContent = (id: string): React.ReactNode => {
        switch (id) {
            case 'savings':
            case 'cash': {
                const types = BRIEFING.impact_discipline.types;
                return <>
                    <div className="db-pop-title">Value identified — two separate totals</div>
                    <div className="db-dim-row">
                        <span className="db-dim-label">
                            {types.find(t => t.id === 'savings')?.label}
                            <span className="db-dim-question">{types.find(t => t.id === 'savings')?.unit}</span>
                        </span>
                        <span className="db-dim-value">{formatCompactEuro(view.savingsTotal)}</span>
                    </div>
                    <div className="db-dim-row">
                        <span className="db-dim-label">
                            {types.find(t => t.id === 'cash_release')?.label}
                            <span className="db-dim-question">{types.find(t => t.id === 'cash_release')?.unit}</span>
                        </span>
                        <span className="db-dim-value">{formatCompactEuro(view.cashTotal)}</span>
                    </div>
                </>;
            }
            case 'waiting':
                return <>
                    <div className="db-pop-title">Waiting on you</div>
                    {BRIEFING.initiatives.tasks_waiting.map(t =>
                        <div key={t.id} className="db-fact-item">
                            <div className="db-fact-claim">{t.label}</div>
                            <div className="db-fact-meta">
                                {t.initiative ?? t.requested_by} · since {t.waiting_since}
                            </div>
                        </div>
                    )}
                </>;
            default:
                return null;
        }
    };

    const userSwitcher = (
        <>
            <Button
                className="db-user-switcher"
                color="inherit"
                size="small"
                endIcon={<ExpandMore/>}
                onClick={e => setSwitcherAnchor(e.currentTarget)}
            >
                {session.user.name}
            </Button>
            <Menu
                anchorEl={switcherAnchor}
                open={Boolean(switcherAnchor)}
                onClose={() => setSwitcherAnchor(null)}
            >
                <MenuItem selected onClick={() => setSwitcherAnchor(null)}>
                    {session.user.name} — {session.user.category}
                </MenuItem>
                <MenuItem disabled>Priya Nair — Professional Services</MenuItem>
            </Menu>
        </>
    );

    return <MainPageFacade
        title={<></>}
        className="daily-briefing-page"
        actionBarEnd={session.user_switch_available ? userSwitcher : undefined}
    >
        <div className="db-layout">
            <div className="db-main">
                <AtlasHero/>

                {state.fixApplied && state.bannerVisible &&
                    <div className="db-diff-banner" role="status">
                        <span>{BRIEFING.human_in_the_loop.diff_banner_example}</span>
                        <IconButton size="small" aria-label="Dismiss update" onClick={() => dispatch({type: 'dismissBanner'})}>
                            <Close fontSize="small"/>
                        </IconButton>
                    </div>
                }

                {state.assumptionBanner &&
                    <div className="db-diff-banner" role="status">
                        <span>{state.assumptionBanner}</span>
                        <IconButton size="small" aria-label="Dismiss update" onClick={() => dispatch({type: 'clearAssumptionBanner'})}>
                            <Close fontSize="small"/>
                        </IconButton>
                    </div>
                }

                <div className="db-section-head">
                    <span className="db-section-title">Daily briefs</span>
                    <span className="db-section-count">{view.readyToAct.length}</span>
                    <span className="db-section-meta">
                        {session.today_display} · {session.scope_label} · {formatCompactEuro(session.user.scope_spend_ltm)} LTM
                    </span>
                </div>

                <h1 className="db-ambient-line">{view.ambientSentence}</h1>

                <div className="db-chips-row">
                    {chips.map(chip =>
                        <ButtonBase key={chip.id} className="db-counter-chip" onClick={e => onChipClick(chip.id, e.currentTarget)}>
                            {chip.icon}
                            <span className="db-chip-text">{chip.label}</span>
                            <b>{chip.value}</b>
                        </ButtonBase>
                    )}
                    <span className="db-list-arrows">
                        <IconButton
                            size="small"
                            aria-label="Previous opportunity"
                            disabled={!listScroll.canPrev}
                            onClick={() => nudgeList(-1)}
                        >
                            <ChevronLeft/>
                        </IconButton>
                        <IconButton
                            size="small"
                            aria-label="Next opportunity"
                            disabled={!listScroll.canNext}
                            onClick={() => nudgeList(1)}
                        >
                            <ChevronRight/>
                        </IconButton>
                    </span>
                </div>

                <div
                    className="db-list"
                    ref={listRef}
                    onScroll={onListScroll}
                    role="region"
                    aria-label="Opportunities today"
                >
                    {view.emptyState
                        ? <div className="db-list-empty">{view.emptyState}</div>
                        : view.readyToAct.map(computed =>
                            <OpportunityCard key={computed.opportunity.id} computed={computed}/>
                        )}
                </div>

                {view.createdInitiatives.length > 0 &&
                    <div className="db-initiative-lines">
                        {view.createdInitiatives.map(c =>
                            <div key={c.opportunity.id} className="db-initiative-line">
                                <CheckCircleOutline sx={{fontSize: 15}}/>
                                {c.opportunity.title} — now a tracked initiative · {formatCompactEuro(c.impact)}{' '}
                                {BRIEFING.impact_discipline.types.find(t => t.id === c.impactType)?.label.toLowerCase()}
                            </div>
                        )}
                    </div>
                }

                {BRIEFING.needs_confirmation.map(opportunity =>
                    <ButtonBase key={opportunity.id} className="db-nc-card" onClick={() => setNeedsConfirmationOpen(true)}>
                        <ScheduleOutlined className="db-nc-card-icon"/>
                        <span className="db-nc-card-title">{opportunity.title}</span>
                        <span className="db-nc-card-meta">
                            {formatCompactEuro(opportunity.potential_impact)} potential impact · {opportunity.confidence}% confidence
                        </span>
                        <ChevronRight className="db-nc-card-chevron"/>
                    </ButtonBase>
                )}
                {view.needsConfirmation.map(c =>
                    <ButtonBase key={c.opportunity.id} className="db-nc-card" onClick={() => setNeedsConfirmationOpen(true)}>
                        <ScheduleOutlined className="db-nc-card-icon"/>
                        <span className="db-nc-card-title">{c.opportunity.title}</span>
                        <span className="db-nc-card-meta">
                            needs confirmation · {c.confidence}% confidence
                        </span>
                        <ChevronRight className="db-nc-card-chevron"/>
                    </ButtonBase>
                )}
            </div>

            {state.atlasOpen && <AtlasCompanion/>}
        </div>

        {!state.atlasOpen &&
            <Button
                className="db-ask-atlas"
                color="inherit"
                startIcon={<AtlasIcon className="db-atlas-mark"/>}
                onClick={() => dispatch({type: 'openAtlas'})}
            >
                {BRIEFING.atlas_panel.hint_label}
            </Button>
        }

        <Popover
            open={Boolean(counterAnchor)}
            anchorEl={counterAnchor?.el}
            onClose={() => setCounterAnchor(null)}
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            transformOrigin={{vertical: 'top', horizontal: 'left'}}
            slotProps={{paper: {className: 'db-pop'}}}
        >
            {counterAnchor && counterPopContent(counterAnchor.id)}
        </Popover>

        <NeedsConfirmationDialog open={needsConfirmationOpen} onClose={() => setNeedsConfirmationOpen(false)}/>
    </MainPageFacade>;
});

export const DailyBriefingPage: React.FC = () => (
    <BriefingProvider>
        <DailyBriefingContent/>
    </BriefingProvider>
);
